<script setup lang="ts">
import type { ISpecialProjectYearItemDataClientDTO } from '~/shared/api/client/special_project_year/special_project_year.dto';
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_SPECIAL } from '~/shared/lib/crop-ratio.const';
import { SPECIAL_ITEM_IMAGE_WIDTH } from '../lib/const';

const props = defineProps<
  ISpecialProjectYearItemDataClientDTO & {
    textColor: string;
    internal?: boolean;
    utm?: string;
  }
>();

const isExpert = computed(
  () => props.expert !== null && props.expert !== undefined,
);

const isRedirect = computed(() =>
  Boolean(props.news?.redirect && props.news.redirect.length > 0),
);

const link = computed(() => {
  if (isRedirect.value && props.news?.redirect)
    return props.news.redirect + (props?.utm ?? '');
  if (isExpert.value)
    return `/expert/${getMaterialPublicId(props.expert!)}${props?.utm ?? ''}`;
  return `/${props.news?.type}/${props.news?.id}${props?.utm ?? ''}`;
});

const image = computed(
  () =>
    props.image_one?.file_url ||
    props.news?.image?.photo.file_url ||
    props.expert?.image?.photo.file_url ||
    undefined,
);
const imageAttrs = computed(() =>
  resizeCropSlot(image.value, CROP_RATIO_SPECIAL.image_big, 0, {
    width: SPECIAL_ITEM_IMAGE_WIDTH,
  }),
);
</script>

<template>
  <div
    :class="$style.item"
    :style="{
      '--text-color': textColor,
    }"
  >
    <NuxtLink
      v-if="props.image_one"
      :class="$style.item__imageWrapper"
      :to="link"
      :target="isRedirect ? '_blank' : undefined"
    >
      <img
        :src="imageAttrs.src"
        :srcset="imageAttrs.srcset || undefined"
        :alt="title"
        :class="$style.item__imageWrapper"
      />
    </NuxtLink>
    <NuxtLink :class="$style.item__title" :to="link">
      {{ props.title || props.news?.title || props.expert?.title }}
    </NuxtLink>

    <div
      v-if="props.is_object"
      :class="[
        $style.item__object,
        { [$style.item__object_internal!]: internal },
      ]"
    >
      Объект
    </div>
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5x);
  position: relative;

  &__title {
    display: block;
    @include text_body1_medium;
    color: var(--text-color);
    text-decoration: none;
    flex-grow: 1;
    align-self: stretch;
    align-content: center;
  }

  &__object {
    position: absolute;
    top: var(--space-2-5x);
    left: 50%;
    width: 155px;
    height: 35px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    @include text_caption2_caps;
    color: var(--color-white);
    background-color: var(--color-red-1);
    text-transform: uppercase;
    z-index: 1;
    &_internal {
      top: 0;
    }
  }

  &__image {
    height: auto;

    &Wrapper {
      display: flex;
      position: relative;
      width: 100%;
      height: 180px;
      object-fit: cover;
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
  .item {
    &__object {
      top: var(--space-2x);
      &_internal {
        top: 0;
      }
    }
  }
}
</style>
